import { useMemo, useState } from "react";
import { ExternalLink, AlertTriangle, Info, CheckCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WpPostRow } from "@/hooks/useExistingPosts";
import type { ClusterFitLabel } from "@/services/wpClusterFitClassifier";

interface Props {
  posts: WpPostRow[];
  onChangeClusterFit: (opts: {
    wpPostId: string;
    newValue: ClusterFitLabel;
    reason?: string;
  }) => void;
  isPending?: boolean;
}

interface SectionConfig {
  label: ClusterFitLabel;
  title: string;
  description: string;
  icon: typeof AlertTriangle;
  badgeClass: string;
  sectionClass: string;
}

const SECTIONS: SectionConfig[] = [
  {
    label: "off_topic",
    title: "Off-topic",
    description:
      "Inna domena semantyczna. Kandydaci do usunięcia (zostają rozważeni w Decision Engine).",
    icon: AlertTriangle,
    badgeClass: "bg-red-500/15 text-red-400 border-red-500/30",
    sectionClass: "border-red-500/30",
  },
  {
    label: "adjacent",
    title: "Adjacent (outer cluster)",
    description:
      "Związek tematyczny ale inna kategoria produktu. Buduje autorytet — zwykle keep.",
    icon: Info,
    badgeClass: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    sectionClass: "border-amber-500/30",
  },
  {
    label: "in_scope",
    title: "In-scope (core cluster)",
    description:
      "Core cluster mapy. Kandydaci na match/refurbish w Content Engine.",
    icon: CheckCircle,
    badgeClass: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    sectionClass: "border-emerald-500/30",
  },
];

const CLASSIFICATION_OPTIONS: ClusterFitLabel[] = ["in_scope", "adjacent", "off_topic"];

function confidenceBadge(confidence: number | null): string {
  if (confidence === null) return "bg-zinc-700/40 text-zinc-400";
  if (confidence >= 0.85) return "bg-emerald-500/15 text-emerald-400";
  if (confidence >= 0.6) return "bg-amber-500/15 text-amber-400";
  return "bg-red-500/15 text-red-400";
}

function truncate(s: string | null, n = 100): string {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function ClusterFitReviewPanel({ posts, onChangeClusterFit, isPending }: Props) {
  const [openSections, setOpenSections] = useState<string[]>([
    "off_topic",
    "adjacent",
  ]);

  const grouped = useMemo(() => {
    const by: Record<ClusterFitLabel, WpPostRow[]> = {
      in_scope: [],
      adjacent: [],
      off_topic: [],
    };
    for (const p of posts) {
      if (p.cluster_fit && by[p.cluster_fit]) {
        by[p.cluster_fit].push(p);
      }
    }
    return by;
  }, [posts]);

  const notClassifiedCount = posts.filter((p) => !p.cluster_fit).length;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">
              Przegląd klasyfikacji scope
            </h3>
            <p className="mt-1 text-xs text-zinc-400">
              Review 3 kubełków: off-topic → kandydaci do usunięcia, adjacent → outer cluster,
              in-scope → core mapy. Możesz ręcznie zmienić klasyfikację (manual override).
            </p>
          </div>
          {notClassifiedCount > 0 && (
            <Badge variant="outline" className="bg-zinc-800 text-zinc-300">
              {notClassifiedCount} nie sklasyfikowane
            </Badge>
          )}
        </div>
      </div>

      <TooltipProvider>
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="space-y-3"
        >
          {SECTIONS.map((section) => {
            const rows = grouped[section.label];
            const Icon = section.icon;
            return (
              <AccordionItem
                key={section.label}
                value={section.label}
                className={`rounded-lg border ${section.sectionClass} bg-zinc-900/30`}
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex flex-1 items-center justify-between pr-4">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-zinc-300" />
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-zinc-100">
                            {section.title}
                          </span>
                          <Badge className={section.badgeClass} variant="outline">
                            {rows.length}
                          </Badge>
                        </div>
                        <p className="mt-0.5 text-xs text-zinc-400">
                          {section.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-4 pb-4">
                  {rows.length === 0 ? (
                    <p className="py-3 text-center text-xs text-zinc-500">
                      Brak wpisów w tym kubełku.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-md border border-zinc-800">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-900/80 text-left text-zinc-400">
                          <tr>
                            <th className="px-3 py-2 font-medium">Tytuł</th>
                            <th className="px-3 py-2 font-medium">Entity</th>
                            <th className="px-3 py-2 font-medium">Confidence</th>
                            <th className="px-3 py-2 font-medium">Reason</th>
                            <th className="px-3 py-2 font-medium">Traffic</th>
                            <th className="px-3 py-2 font-medium">Zmień</th>
                            <th className="px-3 py-2 font-medium" />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((p) => (
                            <tr
                              key={p.id}
                              className="border-t border-zinc-800 hover:bg-zinc-900/60"
                            >
                              <td className="px-3 py-2 align-top text-zinc-200">
                                <div className="max-w-xs">
                                  <div className="line-clamp-2 font-medium">{p.title}</div>
                                  {p.cluster_fit_method === "manual_override" && (
                                    <span className="mt-0.5 inline-block text-[10px] text-violet-400">
                                      manual override
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top text-zinc-300">
                                {p.extracted_entity || "—"}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[11px] ${confidenceBadge(p.cluster_fit_confidence)}`}
                                >
                                  {p.cluster_fit_confidence !== null
                                    ? p.cluster_fit_confidence.toFixed(2)
                                    : "—"}
                                </span>
                              </td>
                              <td className="px-3 py-2 align-top text-zinc-400">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">
                                      {truncate(p.cluster_fit_reason, 80)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-md">
                                    <p className="whitespace-pre-wrap text-xs">
                                      {p.cluster_fit_reason || "Brak reason"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </td>
                              <td className="px-3 py-2 align-top text-zinc-400">
                                {p.seo_organic_traffic !== null &&
                                p.seo_organic_traffic > 0
                                  ? `~${p.seo_organic_traffic}`
                                  : "—"}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <Select
                                  value={p.cluster_fit ?? undefined}
                                  onValueChange={(v: ClusterFitLabel) =>
                                    onChangeClusterFit({
                                      wpPostId: p.id,
                                      newValue: v,
                                      reason: "Zmiana z panelu Scope",
                                    })
                                  }
                                  disabled={isPending}
                                >
                                  <SelectTrigger className="h-7 w-[120px] text-xs">
                                    <SelectValue placeholder="—" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CLASSIFICATION_OPTIONS.map((opt) => (
                                      <SelectItem key={opt} value={opt}>
                                        {opt}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-3 py-2 align-top">
                                {p.wp_url && (
                                  <Button
                                    asChild
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                  >
                                    <a
                                      href={p.wp_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center gap-1"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </TooltipProvider>
    </div>
  );
}
